

import React, { useEffect, useRef, useState } from "react";

import {

  useLocation,

  useNavigate,

  useParams,

} from "react-router-dom";

import { useDispatch } from "react-redux";

import {

  ArrowLeft,

  ArrowRight,

  CheckCircle2,

  CreditCard,

  Leaf,

  LockKeyhole,

  ShieldCheck,

  ShoppingBag,

  Smartphone,

} from "lucide-react";

import {

  createRazorpayOrder,

  verifyRazorpayPayment,

} from "../../actions/paymentActions";

import { useCart } from "../../components/CartContext";
import { useRestaurantSession } from "../../components/RestaurantSessionProvider";

import "./css/PaymentScreen.css";

/* ============================================

   RAZORPAY SCRIPT LOADER

\============================================ */

const RAZORPAY_SCRIPT_URL =

  "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {

  return new Promise((resolve) => {

    if (window.Razorpay) {

      resolve(true);

      return;

    }

    const existingScript = document.querySelector(

      `script[src="${RAZORPAY_SCRIPT_URL}"]`

    );

    if (existingScript) {

      existingScript.addEventListener(

        "load",

        () => resolve(Boolean(window.Razorpay)),

        { once: true }

      );

      existingScript.addEventListener(

        "error",

        () => resolve(false),

        { once: true }

      );

      return;

    }

    const script = document.createElement("script");

    script.src = RAZORPAY_SCRIPT_URL;

    script.async = true;

    script.onload = () =>

      resolve(Boolean(window.Razorpay));

    script.onerror = () => resolve(false);

    document.body.appendChild(script);

  });

}

const formatPrice = (value) =>

  Number(value || 0).toFixed(2);

/* ============================================

   PAYMENT SCREEN

\============================================ */

function PaymentScreen() {

  const navigate = useNavigate();

  const location = useLocation();

  const dispatch = useDispatch();

  const { restaurantId, tableId } = useParams();

  const { clearCart } = useCart();
  const { sessionToken } = useRestaurantSession();

  const {

    orderId,

    subtotal = 0,

    tax = 0,

    serviceCharge = 0,

    total = 0,

  } = location.state || {};

  const [loading, setLoading] = useState(false);

  const [verifying, setVerifying] = useState(false);

  const [paymentError, setPaymentError] = useState("");

  const [requiresReview, setRequiresReview] = useState(false);

  const paymentInProgress = useRef(false);

  const verificationInProgress = useRef(false);

  const homePath =

    `/${restaurantId}/table/${tableId}`;

  const validOrder = Boolean(orderId && sessionToken);

  useEffect(() => {

    if (!orderId) {

      setPaymentError(

        "Order or dining session is missing. Please return to checkout."

      );

    }

  }, [orderId]);

  /* ============================================

     VERIFY PAYMENT WITH DJANGO

  \============================================ */

  const verifyPayment = async (

    razorpayResponse,

    paymentOrder

  ) => {

    if (verificationInProgress.current) return;
  if (!sessionToken) {
    setPaymentError("Dining session is unavailable. Please scan the table QR code again.");
    return;
  }

    verificationInProgress.current = true;

    setVerifying(true);

    setLoading(true);

    setPaymentError("");

    try {

      const verificationData = {

        order_id: orderId,

        razorpay_order_id:

          razorpayResponse.razorpay_order_id,

        razorpay_payment_id:

          razorpayResponse.razorpay_payment_id,

        razorpay_signature:

          razorpayResponse.razorpay_signature,

      };

      await dispatch(

        verifyRazorpayPayment(

          restaurantId,

          verificationData,
          sessionToken

        )

      );

      /* CLEAR CART ONLY AFTER VERIFIED SUCCESS */

      clearCart();

      navigate(`${homePath}/success`, {

        replace: true,

        state: {

          orderId,

          orderNumber: paymentOrder.order_number,

          paymentMethod: "ONLINE",

          total: Number(paymentOrder?.amount) / 100,

          paymentId:

            razorpayResponse.razorpay_payment_id,

        },

      });

    } catch (error) {

      console.error("Payment verification error:", error);

      setRequiresReview(true);

      setPaymentError(

        "Payment verification could not be confirmed. " +

        "If money was deducted, do not pay again. " +

        "Please contact the restaurant with order #" +

        orderId +

        " so they can check the payment status."

      );

    } finally {

      verificationInProgress.current = false;

      paymentInProgress.current = false;

      setVerifying(false);

      setLoading(false);

    }

  };

  /* ============================================

     OPEN RAZORPAY CHECKOUT

  \============================================ */

  const handlePayment = async () => {

    

    if (

      !validOrder ||

      paymentInProgress.current ||

      requiresReview

    ) {

      return;

    }

    paymentInProgress.current = true;

    setLoading(true);

    setPaymentError("");

    try {

      const razorpayLoaded =

        await loadRazorpayScript();

      if (!razorpayLoaded) {

        throw new Error(

          "Razorpay Checkout could not be loaded. " +

          "Please check your internet connection and try again."

        );

      }

      /* CREATE PAYMENT ORDER THROUGH DJANGO */

      const paymentOrder = await dispatch(

        createRazorpayOrder(restaurantId, orderId, sessionToken)

      );

      if (!paymentOrder?.razorpay_order_id) {

        throw new Error(

          "Razorpay order ID was not returned by the server."

        );

      }

      const amount = Number(paymentOrder.amount);

      if (!Number.isSafeInteger(amount) || amount <= 0) {

        throw new Error(

          "A valid payment amount was not returned by the server."

        );

      }

      const razorpayKey =

        paymentOrder.key_id ||

        paymentOrder.razorpay_key_id ||

        paymentOrder.key;

      if (!razorpayKey) {

        throw new Error(

          "Razorpay key was not returned by the server."

        );

      }

      const options = {

        key: razorpayKey,

        amount,

        currency: paymentOrder.currency || "INR",

        name:

          paymentOrder.restaurant_name ||

          "Biryani House",

        description: `Order ${

          paymentOrder.order_number || orderId

        }`,

        order_id: paymentOrder.razorpay_order_id,

        /* THEME 10 RAZORPAY COLOR */

        theme: {

          color: "#657044",

        },

        /* PAYMENT SUCCESS CALLBACK */

        handler: async (response) => {

          await verifyPayment(response, paymentOrder);

        },

        modal: {

          ondismiss: () => {

            if (!verificationInProgress.current) {

              paymentInProgress.current = false;

              setLoading(false);

            }

          },

          escape: true,

          backdropclose: false,

        },

        retry: {

          enabled: true,

          max_count: 3,

        },

      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", (response) => {

        console.error(

          "Razorpay payment failed:",

          response

        );

        setPaymentError(

          response?.error?.description ||

          "Payment was unsuccessful. Please try again."

        );

        /* Keep cart and existing order */

        if (!verificationInProgress.current) {

          paymentInProgress.current = false;

          setLoading(false);

        }

      });

      razorpay.open();

    } catch (error) {

      console.error("Payment error:", error);

      setPaymentError(

        error?.response?.data?.detail ||

        error?.message ||

        "Unable to start payment."

      );

      paymentInProgress.current = false;

      setLoading(false);

    }

  };

  /* ============================================

     BACK NAVIGATION

  \============================================ */

  const handleBack = () => {

    if (loading || requiresReview) return;

    navigate(`${homePath}/order`, {

      state: {

        subtotal: Number(subtotal),

        tax: Number(tax),

        serviceCharge: Number(serviceCharge),

        total: Number(total),

      },

    });

  };

  return (

    <main className="theme10-payment-page">

      {/* HEADER */}

      <section className="theme10-payment-header">

        <div className="theme10-payment-container">

          <button

            type="button"

            className="theme10-payment-back"

            onClick={handleBack}

            disabled={loading || requiresReview}

          >

            <ArrowLeft size={17} />

            Back to Order

          </button>

          <div className="theme10-payment-title-row">

            <div className="theme10-payment-heading">

              <span className="theme10-payment-label">

                SAFE & SECURE CHECKOUT

              </span>

              <h1>

                Complete Your <em>Payment.</em>

              </h1>

              <p>

                Just one more step before your delicious

                meal is on its way.

              </p>

            </div>

            <div className="theme10-payment-table">

              <span>YOUR TABLE</span>

              <strong>{tableId}</strong>

            </div>

          </div>

        </div>

        <div className="theme10-payment-header-decoration" />

      </section>

      {/* CONTENT */}

      <section className="theme10-payment-content">

        <div className="theme10-payment-container">

          <div className="theme10-payment-layout">

            {/* LEFT SIDE */}

            <div className="theme10-payment-main">

              <div className="theme10-payment-intro">

                <span className="theme10-payment-step">

                  01

                </span>

                <div>

                  <small>ONLINE PAYMENT</small>

                  <h2>Pay with confidence</h2>

                  <p>

                    Complete your payment to confirm

                    your restaurant order.

                  </p>

                </div>

              </div>

              {/* RAZORPAY CARD */}

              <div className="theme10-payment-brand-card">

                <div className="theme10-payment-brand-icon">

                  ₹

                </div>

                <div className="theme10-payment-brand-info">

                  <span>SECURE CHECKOUT WITH</span>

                  <h2>Razorpay</h2>

                  <p>

                    Convenient online payment for

                    your restaurant order.

                  </p>

                </div>

                <div className="theme10-payment-brand-status">

                  <CheckCircle2 size={17} />

                  <span>READY</span>

                </div>

              </div>

              {/* PAYMENT METHODS */}

              <div className="theme10-payment-methods">

                <div className="theme10-payment-method">

                  <span className="theme10-payment-method-icon">

                    <CreditCard size={21} />

                  </span>

                  <div>

                    <strong>Cards</strong>

                    <p>Credit & debit cards</p>

                  </div>

                </div>

                <div className="theme10-payment-method">

                  <span className="theme10-payment-method-icon">

                    <Smartphone size={21} />

                  </span>

                  <div>

                    <strong>UPI & More</strong>

                    <p>

                      Options shown in Razorpay

                    </p>

                  </div>

                </div>

                <div className="theme10-payment-method">

                  <span className="theme10-payment-method-icon">

                    <ShieldCheck size={21} />

                  </span>

                  <div>

                    <strong>Protected</strong>

                    <p>Secure payment processing</p>

                  </div>

                </div>

              </div>

              {/* SECURITY INFO */}

              <div className="theme10-payment-security">

                <div className="theme10-payment-security-icon">

                  <LockKeyhole size={23} />

                </div>

                <div>

                  <small>PAYMENT PROTECTION</small>

                  <strong>Secure online checkout</strong>

                  <p>

                    Your payment is processed through

                    Razorpay. Card details are not

                    collected by this order screen.

                  </p>

                </div>

              </div>

              {/* ORDER REFERENCE */}

              <div className="theme10-payment-reference">

                <div className="theme10-payment-reference-icon">

                  <ShoppingBag size={20} />

                </div>

                <div className="theme10-payment-reference-info">

                  <span>YOUR ORDER REFERENCE</span>

                  <strong>

                    Order #{orderId || "—"}

                  </strong>

                </div>

                <div className="theme10-payment-reference-table">

                  <span>TABLE</span>

                  <strong>{tableId}</strong>

                </div>

              </div>

              {/* ERROR */}

              {paymentError && (

                <div

                  className="theme10-payment-error"

                  role="alert"

                >

                  <strong>

                    {requiresReview

                      ? "Payment status needs checking"

                      : "Unable to complete payment"}

                  </strong>

                  <p>{paymentError}</p>

                </div>

              )}

              {/* PAYMENT BUTTON */}

              <button

                type="button"

                className="theme10-pay-now-button"

                onClick={handlePayment}

                disabled={

                  loading ||

                  !validOrder ||

                  requiresReview

                }

              >

                {loading ? (

                  <>

                    <span className="theme10-payment-spinner" />

                    {verifying

                      ? "Verifying Your Payment..."

                      : "Opening Secure Payment..."}

                  </>

                ) : (

                  <>

                    <span>

                      Pay ₹{formatPrice(total)}

                    </span>

                    <ArrowRight size={19} />

                  </>

                )}

              </button>

              <div className="theme10-payment-bottom-note">

                <LockKeyhole size={15} />

                <span>

                  Secure checkout powered by Razorpay

                </span>

              </div>

            </div>

            {/* RIGHT SUMMARY */}

            <aside className="theme10-payment-summary">

              <div className="theme10-payment-summary-top">

                <div className="theme10-payment-summary-icon">

                  <ShoppingBag size={22} />

                </div>

                <div>

                  <span className="theme10-payment-summary-label">

                    02 · ORDER SUMMARY

                  </span>

                  <h2>

                    Your <em>Order.</em>

                  </h2>

                </div>

              </div>

              <p className="theme10-payment-summary-subtitle">

                Final amount for your table order.

              </p>

              <div className="theme10-payment-price-row">

                <span>Subtotal</span>

                <strong>

                  ₹{formatPrice(subtotal)}

                </strong>

              </div>

              <div className="theme10-payment-price-row">

                <span>Tax</span>

                <strong>₹{formatPrice(tax)}</strong>

              </div>

              <div className="theme10-payment-price-row">

                <span>Service Charge</span>

                <strong>

                  ₹{formatPrice(serviceCharge)}

                </strong>

              </div>

              <div className="theme10-payment-divider" />

              <div className="theme10-payment-total">

                <div>

                  <span>TOTAL TO PAY</span>

                  <small>

                    Including applicable charges

                  </small>

                </div>

                <strong>

                  ₹{formatPrice(total)}

                </strong>

              </div>

              <div className="theme10-payment-order-info">

                <div>

                  <span>TABLE NUMBER</span>

                  <strong>{tableId}</strong>

                </div>

                <div className="theme10-payment-order-status">

                  <CheckCircle2 size={16} />

                  <span>Order created</span>

                </div>

              </div>

              <div className="theme10-payment-summary-security">

                <ShieldCheck size={18} />

                <p>

                  Your payment amount is generated

                  and verified by the server.

                </p>

              </div>

              <div className="theme10-payment-fresh-note">

                <Leaf size={17} />

                <span>

                  Your meal will be prepared

                  after payment confirmation.

                </span>

              </div>

            </aside>

          </div>

        </div>

      </section>

    </main>

  );

}

export default PaymentScreen;